import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Flag, Plus, Trash2 } from 'lucide-react';
import { settingsApi } from '@/api/settings';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';
import type { AdminFeatureFlag } from '@/types';

function stringifyValue(value: AdminFeatureFlag['value']): string {
  return JSON.stringify(value ?? null, null, 2);
}

export default function FeatureFlagsSection() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [valueText, setValueText] = useState('true');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['settings-feature-flags'],
    queryFn: settingsApi.listFeatureFlags,
    staleTime: 15_000,
  });

  const closeModal = () => {
    setModalOpen(false);
    setEditingKey(null);
    setKeyName('');
    setValueText('true');
    setError('');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(valueText);
      const key = (editingKey ?? keyName).trim();
      if (!key) throw new Error('Flag key is required');
      return settingsApi.upsertFeatureFlag(key, parsed);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-feature-flags'] });
      closeModal();
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: settingsApi.deleteFeatureFlag,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings-feature-flags'] });
    },
  });

  return (
    <>
      <section className="card p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">Feature flags</h2>
            <p className="mt-1 text-sm text-ink-secondary">
              Simple JSON-backed flags owned by the admin portal.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => {
              closeModal();
              setModalOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add flag
          </Button>
        </div>

        {isLoading ? (
          <Loading text="Loading feature flags..." />
        ) : !data?.length ? (
          <EmptyState
            icon={<Flag className="h-8 w-8" />}
            title="No feature flags"
            description="Create feature flags here and consume them at request time."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-border text-left text-xs text-ink-muted">
                  <th className="py-3 pr-4">Key</th>
                  <th className="py-3 pr-4">Value</th>
                  <th className="py-3 pr-4">Updated</th>
                  <th className="py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-border">
                {data.map((flag) => (
                  <tr key={flag.key}>
                    <td className="py-3 pr-4 font-mono text-xs text-ink-primary">{flag.key}</td>
                    <td className="py-3 pr-4 text-xs text-ink-secondary">
                      <code className="block max-w-[420px] whitespace-pre-wrap break-words">
                        {stringifyValue(flag.value)}
                      </code>
                    </td>
                    <td className="py-3 pr-4 text-ink-secondary">{new Date(flag.updated_at).toLocaleString()}</td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setEditingKey(flag.key);
                            setKeyName(flag.key);
                            setValueText(stringifyValue(flag.value));
                            setError('');
                            setModalOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => void deleteMutation.mutateAsync(flag.key)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingKey ? 'Edit feature flag' : 'Add feature flag'}
        footer={(
          <>
            <Button variant="secondary" onClick={closeModal} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={() => void saveMutation.mutateAsync()} loading={saveMutation.isPending}>
              Save
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="Flag key"
            value={keyName}
            disabled={!!editingKey}
            onChange={(event) => setKeyName(event.target.value)}
            placeholder="ENFORCE_IMPERSONATION_CONSENT"
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">JSON value</label>
            <textarea
              value={valueText}
              onChange={(event) => setValueText(event.target.value)}
              rows={8}
              className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 font-mono text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-gold/40"
            />
          </div>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
        </div>
      </Modal>
    </>
  );
}
